import { createContext, useCallback, useContext, useMemo, useState, FC } from 'react';

import { CurrentUserContext } from './CurrentUserContext';
import { getCurrentUserId } from 'selectors';

/** 'default' follows whatever the company has set; the other two override it for this browser. */
export type TabNavPreference = 'default' | 'on' | 'off';

const STORAGE_PREFIX = 'simplify.tabNav';

const storageKey = (userId: number) => `${STORAGE_PREFIX}.${userId}`;

export const readStoredPreference = (userId: number): TabNavPreference => {
  try {
    const stored = localStorage.getItem(storageKey(userId));
    return stored === 'on' || stored === 'off' ? stored : 'default';
  } catch {
    // Private browsing and locked-down profiles can throw on access; the company default is fine.
    return 'default';
  }
};

export interface TabNavSettingContextData {
  preference: TabNavPreference;
  setPreference: (preference: TabNavPreference) => void;
}

export const TabNavSettingContext = createContext<TabNavSettingContextData>({
  preference: 'default',
  setPreference: () => {}
});

/**
 * The user's own choice about the tab bar, kept per browser.
 *
 * It lives in React state rather than being read from localStorage where it is needed: a write to
 * localStorage renders nothing, so a toggle backed only by storage would appear to do nothing until
 * the page was reloaded.
 *
 * The stored value is per user, and the user is not known on the first render — App loads the
 * current user from the API, so `userId` is 0 until that resolves. The read is therefore tied to
 * the id it was made with and repeated once the real one arrives; reading only at mount looked up
 * an empty key and quietly dropped the choice on every reload. Adjusting state during render
 * rather than in an effect keeps the bar from flashing on for someone who switched it off.
 */
export const TabNavSettingProvider: FC = props => {
  const { children } = props;
  const { currentUser } = useContext(CurrentUserContext);
  const userId = getCurrentUserId(currentUser);

  const [stored, setStored] = useState<{ userId: number; preference: TabNavPreference }>(() => ({
    userId,
    preference: readStoredPreference(userId)
  }));

  if (stored.userId !== userId) {
    setStored({ userId, preference: readStoredPreference(userId) });
  }

  const preference = stored.preference;

  const setPreference = useCallback(
    (next: TabNavPreference) => {
      setStored({ userId, preference: next });

      try {
        if (next === 'default') {
          localStorage.removeItem(storageKey(userId));
        } else {
          localStorage.setItem(storageKey(userId), next);
        }
      } catch {
        // Storage refused the write, so the choice lasts for this session only.
      }
    },
    [userId]
  );

  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);

  return <TabNavSettingContext.Provider value={value}>{children}</TabNavSettingContext.Provider>;
};

export const useTabNavPreference = (): TabNavSettingContextData => useContext(TabNavSettingContext);
